/** @file SvgTextLabel
 * From https://github.com/tldraw/tldraw/blob/8bbfe91e8805198827a48c5410d7e741f416ac1d/packages/tldraw/src/lib/shapes/shared/SvgTextLabel.tsx
 */

import { createTextJsxFromSpans } from '@@/bits/tldraw/PadletSpeechBubbleTool/createTextJsxFromSpans'
import type {
  BoxModel,
  TLDefaultFontStyle,
  TLDefaultHorizontalAlignStyle,
  TLDefaultVerticalAlignStyle,
} from '@tldraw/editor'
import { Box, DefaultFontFamilies, useEditor } from '@tldraw/editor'
import { TEXT_PROPS, useDefaultColorTheme } from 'tldraw'

export function getLegacyOffsetX(
  align: TLDefaultHorizontalAlignStyle | string,
  padding: number,
  spans: Array<{ text: string; box: BoxModel }>,
  totalWidth: number,
): number | undefined {
  if ((align === 'start-legacy' || align === 'end-legacy') && spans.length !== 0) {
    const spansBounds = Box.From(spans[0].box)
    for (const { box } of spans) {
      spansBounds.union(box)
    }
    if (align === 'start-legacy') {
      return (totalWidth - 2 * padding - spansBounds.width) / 2
    } else if (align === 'end-legacy') {
      return -(totalWidth - 2 * padding - spansBounds.width) / 2
    }
  }
  return undefined
}

export function SvgTextLabel({
  fontSize,
  font,
  align,
  verticalAlign,
  text,
  labelColor,
  bounds,
  padding = 16,
  stroke = true,
}: {
  fontSize: number
  font: TLDefaultFontStyle
  // fill?: TLDefaultFillStyle
  align: TLDefaultHorizontalAlignStyle
  verticalAlign: TLDefaultVerticalAlignStyle
  wrap?: boolean
  text: string
  labelColor: string
  bounds: Box
  padding?: number
  stroke?: boolean
}): JSX.Element {
  const editor = useEditor()
  const theme = useDefaultColorTheme()

  const opts = {
    fontSize,
    fontFamily: DefaultFontFamilies[font],
    textAlign: align,
    verticalTextAlign: verticalAlign,
    width: Math.ceil(bounds.width),
    height: Math.ceil(bounds.height),
    padding,
    lineHeight: TEXT_PROPS.lineHeight,
    fontStyle: 'normal',
    fontWeight: 'normal',
    overflow: 'wrap' as const,
    offsetX: 0,
    offsetY: 0,
    fill: labelColor,
    stroke: undefined as string | undefined,
    strokeWidth: undefined as number | undefined,
  }

  const spans = editor.textMeasure.measureTextSpans(text, opts)
  const offsetX = getLegacyOffsetX(align, padding, spans, bounds.width)
  if (offsetX != null) {
    opts.offsetX = offsetX
  }

  opts.offsetX += bounds.x
  opts.offsetY += bounds.y

  const mainSpans = createTextJsxFromSpans(editor, spans, opts)

  let outlineSpans: JSX.Element | null = null
  if (stroke) {
    opts.fill = theme.background
    opts.stroke = theme.background
    opts.strokeWidth = 2
    outlineSpans = createTextJsxFromSpans(editor, spans, opts)
  }

  return (
    <>
      {outlineSpans}
      {mainSpans}
    </>
  )
}
